"use client";

import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { encodeFilePathForApi, getFileName, getRelativeFilePath } from "@/lib/file-paths";

interface Props {
  filePath: string;
  cwd?: string;
  onClose: () => void;
  onSaved?: () => void;
}

type FileData = {
  content: string;
  language: string;
  size: number;
};

export function ProfileFileEditor({ filePath, cwd, onClose, onSaved }: Props) {
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [language, setLanguage] = useState("text");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dirty = content !== original;
  const encoded = encodeFilePathForApi(filePath);
  const isMarkdown = language === "markdown" || getFileName(filePath).toLowerCase().endsWith(".md");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`/api/files/${encoded}?type=read`);
      const data = await res.json() as FileData & { error?: string };
      if (!res.ok || data.error) throw new Error(data.error ?? `HTTP ${res.status}`);
      setContent(data.content);
      setOriginal(data.content);
      setLanguage(data.language);
      setMode(data.language === "markdown" ? "preview" : "edit");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [encoded]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`/api/files/${encoded}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok || data.error) throw new Error(data.error ?? `HTTP ${res.status}`);
      setOriginal(content);
      setNotice("档案已保存。");
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteFile = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      setNotice(null);
      setError(null);
      return;
    }
    setDeleting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`/api/files/${encoded}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok || data.error) throw new Error(data.error ?? `HTTP ${res.status}`);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-editor-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 850,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15,23,42,0.32)",
        padding: 20,
      }}
      onClick={() => {
        if (!dirty) onClose();
      }}
    >
      <div
        style={{
          width: "min(860px, 100%)",
          height: "min(720px, 92vh)",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg-panel)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          boxShadow: "0 24px 70px rgba(15,23,42,0.24)",
          color: "var(--text)",
          overflow: "hidden",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 id="profile-editor-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{getFileName(filePath)}</h2>
            <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={filePath}>
              {getRelativeFilePath(filePath, cwd)} · {language}
            </div>
          </div>
          {notice && <span style={{ fontSize: 12, color: "#16a34a" }}>{notice}</span>}
          {error && <span style={{ fontSize: 12, color: "#ef4444" }}>{error}</span>}
          {isMarkdown && (
            <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 7, overflow: "hidden", flexShrink: 0 }}>
              <button
                onClick={() => setMode("preview")}
                style={{
                  height: 32,
                  padding: "0 11px",
                  border: "none",
                  background: mode === "preview" ? "var(--bg-selected)" : "var(--bg)",
                  color: mode === "preview" ? "var(--text)" : "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: mode === "preview" ? 650 : 500,
                }}
              >
                预览
              </button>
              <button
                onClick={() => setMode("edit")}
                style={{
                  height: 32,
                  padding: "0 11px",
                  border: "none",
                  borderLeft: "1px solid var(--border)",
                  background: mode === "edit" ? "var(--bg-selected)" : "var(--bg)",
                  color: mode === "edit" ? "var(--text)" : "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: mode === "edit" ? 650 : 500,
                }}
              >
                编辑
              </button>
            </div>
          )}
          <button
            onClick={deleteFile}
            disabled={loading || saving || deleting}
            style={{
              height: 32,
              padding: "0 12px",
              border: "1px solid rgba(239,68,68,0.24)",
              borderRadius: 7,
              background: confirmingDelete ? "rgba(239,68,68,0.10)" : "var(--bg)",
              color: confirmingDelete ? "#ef4444" : "var(--text-muted)",
              cursor: loading || saving || deleting ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 650,
            }}
          >
            {deleting ? "删除中..." : confirmingDelete ? "确认删除" : "删除"}
          </button>
          <button
            onClick={save}
            disabled={loading || saving || !dirty}
            style={{
              height: 32,
              padding: "0 12px",
              border: "none",
              borderRadius: 7,
              background: dirty ? "var(--accent)" : "var(--bg)",
              color: dirty ? "#fff" : "var(--text-muted)",
              cursor: loading || saving || !dirty ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 650,
            }}
          >
            {saving ? "保存中..." : dirty ? "保存" : "已保存"}
          </button>
          <button
            onClick={onClose}
            title="关闭"
            aria-label="关闭"
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              borderRadius: 7,
              background: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: "grid", placeItems: "center", color: "var(--text-muted)", fontSize: 13 }}>正在加载档案...</div>
        ) : isMarkdown && mode === "preview" ? (
          <div style={{ flex: 1, overflow: "auto", background: "var(--bg)", padding: "22px 28px" }}>
            <div className="markdown-body markdown-file-preview" style={{ maxWidth: 760 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <textarea
            value={content}
            onChange={(event) => {
              setContent(event.target.value);
              setNotice(null);
              setConfirmingDelete(false);
            }}
            spellCheck={false}
            style={{
              flex: 1,
              width: "100%",
              border: "none",
              outline: "none",
              resize: "none",
              background: "var(--bg)",
              color: "var(--text)",
              padding: 16,
              fontSize: 13,
              lineHeight: 1.65,
              fontFamily: "var(--font-mono)",
            }}
          />
        )}
      </div>
    </div>
  );
}
